import React, { useState } from 'react';
import './AIAssistant.css';
import { askClaude } from '../services/claudeApi';

const AIAssistant = ({ accounts }) => {
  const [input, setInput] = useState('');
  // Updated to use dynamic account data from CSV imports
  const [response, setResponse] = useState("I can analyze your accounts using Claude AI. Ask me about health scores, trends, or which accounts need attention.");
  const [isLoading, setIsLoading] = useState(false);
  const [useClaudeAPI, setUseClaudeAPI] = useState(true);

  const getAccountStatus = async () => {
    if (!input.trim()) {
      setResponse("Please enter a question about your accounts.");
      return;
    }

    setIsLoading(true);
    
    if (useClaudeAPI) {
      try {
        const claudeResponse = await askClaude(input, accounts);
        setResponse(claudeResponse);
        setIsLoading(false);
      } catch (error) {
        console.error('Claude API error:', error);
        if (error.message.includes('API key')) {
          setResponse("Please set your Claude API key in the .env file. Using fallback mode...");
          setUseClaudeAPI(false);
          setTimeout(() => {
            processAccountQuery();
            setIsLoading(false);
          }, 100);
        } else {
          setResponse(`Error: ${error.message}. Using fallback mode...`);
          setTimeout(() => {
            processAccountQuery();
            setIsLoading(false);
          }, 100);
        }
      }
    } else {
      processAccountQuery();
      setIsLoading(false);
    }
  };

  const processAccountQuery = () => {

    // Find matching account
    let matchedAccount = null;
    Object.entries(accounts).forEach(([key, account]) => {
      if (input.toLowerCase().includes(account.name.toLowerCase())) {
        matchedAccount = account;
      }
    });

    if (matchedAccount) {
      const statusEmoji = matchedAccount.status === 'healthy' ? '✅' : 
                        matchedAccount.status === 'neutral' ? '⚠️' : '🚨';
      
      const highPriorityTickets = matchedAccount.tickets.filter(t => t.priority === 'high').length;
      
      const statusReport = `
        <strong>${statusEmoji} ${matchedAccount.name} Status Report</strong><br><br>
        <strong>Health Score:</strong> ${matchedAccount.healthScore}% (${matchedAccount.status})<br>
        <strong>Open Tickets:</strong> ${matchedAccount.tickets.length} total${highPriorityTickets > 0 ? ` (${highPriorityTickets} high priority)` : ''}<br>
        <strong>Summary:</strong> ${matchedAccount.summary}<br><br>
        <strong>Immediate Action:</strong> ${matchedAccount.meetings[0].actions.find(a => !a.completed)?.text || 'All actions completed'}
      `;
      
      setResponse(statusReport);
    } else {
      // Get list of actual account names
      const accountNames = Object.values(accounts).slice(0, 3).map(a => a.name).join(', ');
      setResponse(`I couldn't find that account. Try asking about one of your accounts like: ${accountNames}, etc.`);
    }
    
    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      getAccountStatus();
    }
  };

  return (
    <div className="ai-assistant" role="region" aria-label="AI Assistant">
      <h3>Account Status Assistant</h3>
      <div className="ai-input-group">
        <input 
          type="text" 
          className="ai-input" 
          placeholder="Ask anything about your accounts (e.g., 'Which accounts need attention?')"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          aria-label="Account status query"
          disabled={isLoading}
        />
        <button 
          className="ai-button" 
          onClick={getAccountStatus}
          disabled={isLoading}
          aria-label="Get account status"
        >
          {isLoading ? 'Processing...' : 'Get Status'}
        </button>
      </div>
      <div 
        className={`ai-response ${isLoading ? 'loading' : ''}`}
        role="status"
        aria-live="polite"
        aria-busy={isLoading}
      >
        {response.split('\n').map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>
    </div>
  );
};

export default AIAssistant;